import React from 'react';
import { Link } from 'react-router-dom';
import { FaRocket, FaUsers, FaAward, FaChartLine } from 'react-icons/fa';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gray-50 overflow-hidden font-sans">
      {/* Immersive Hero Section */}
      <section className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] text-white pt-28 pb-40 lg:pt-36 lg:pb-48">
        {/* Abstract Geometric Blurs */}
        <div className="absolute top-0 right-0 -mr-32 -mt-32 w-[30rem] h-[30rem] rounded-full bg-white opacity-10 blur-3xl mix-blend-overlay"></div>
        <div className="absolute bottom-0 left-0 -ml-32 -mb-32 w-[24rem] h-[24rem] rounded-full bg-white opacity-10 blur-2xl mix-blend-overlay"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10 animate-fadeIn">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tight">About Teztech</h1>
          <p className="text-xl md:text-3xl max-w-4xl mx-auto font-medium text-teal-50 leading-relaxed">
            One Complete Solution For Your Growth. Your Complete Platform for networking and building the Future.
          </p>
        </div>
      </section>

      {/* Floating Mission Card */}
      <section className="relative z-20 -mt-24 mb-24 px-4">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-10 md:p-16 border border-gray-100 transform transition-all duration-500 hover:shadow-[0_30px_60px_rgba(0,194,171,0.15)] hover:-translate-y-2">
            <h2 className="text-4xl font-extrabold text-center mb-8 text-gray-900">Our Mission</h2>
            <p className="text-xl md:text-2xl text-gray-600 text-center leading-relaxed">
              At Teztech, we're dedicated to connecting talented professionals with exceptional opportunities. 
              Our platform bridges the gap between job seekers and employers, creating meaningful career paths 
              and helping businesses find the perfect talent.
            </p>
          </div>
        </div>
      </section>

      {/* Vibrant Stats Section */}
      <section className="py-16 mb-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center max-w-6xl mx-auto">
            <div className="p-6">
              <div className="text-5xl md:text-6xl font-extrabold text-[#00c2ab] mb-3">250K+</div>
              <div className="text-lg font-bold text-gray-500 uppercase tracking-wider">Active Jobs</div>
            </div>
            <div className="p-6">
              <div className="text-5xl md:text-6xl font-extrabold text-[#00c2ab] mb-3">10K+</div>
              <div className="text-lg font-bold text-gray-500 uppercase tracking-wider">Companies</div>
            </div>
            <div className="p-6">
              <div className="text-5xl md:text-6xl font-extrabold text-[#00c2ab] mb-3">500K+</div>
              <div className="text-lg font-bold text-gray-500 uppercase tracking-wider">Job Seekers</div>
            </div>
            <div className="p-6">
              <div className="text-5xl md:text-6xl font-extrabold text-[#00c2ab] mb-3">95%</div>
              <div className="text-lg font-bold text-gray-500 uppercase tracking-wider">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Glassmorphic Values Cards */}
      <section className="py-20 bg-white relative">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(#00c2ab 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-16 text-gray-900">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="bg-gray-50 p-10 rounded-3xl text-center transform transition-all duration-300 hover:-translate-y-3 hover:shadow-2xl hover:bg-white border border-transparent hover:border-teal-100 group">
              <div className="w-20 h-20 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00c2ab] group-hover:scale-110 transition-all duration-300">
                <FaRocket className="text-4xl text-[#00c2ab] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Innovation</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Constantly evolving to provide cutting-edge solutions for career growth.
              </p>
            </div>
            
            <div className="bg-gray-50 p-10 rounded-3xl text-center transform transition-all duration-300 hover:-translate-y-3 hover:shadow-2xl hover:bg-white border border-transparent hover:border-teal-100 group">
              <div className="w-20 h-20 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00c2ab] group-hover:scale-110 transition-all duration-300">
                <FaUsers className="text-4xl text-[#00c2ab] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Community</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Building strong connections between professionals and employers.
              </p>
            </div>

            <div className="bg-gray-50 p-10 rounded-3xl text-center transform transition-all duration-300 hover:-translate-y-3 hover:shadow-2xl hover:bg-white border border-transparent hover:border-teal-100 group">
              <div className="w-20 h-20 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00c2ab] group-hover:scale-110 transition-all duration-300">
                <FaAward className="text-4xl text-[#00c2ab] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Excellence</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Committed to delivering the highest quality service and results.
              </p>
            </div>

            <div className="bg-gray-50 p-10 rounded-3xl text-center transform transition-all duration-300 hover:-translate-y-3 hover:shadow-2xl hover:bg-white border border-transparent hover:border-teal-100 group">
              <div className="w-20 h-20 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#00c2ab] group-hover:scale-110 transition-all duration-300">
                <FaChartLine className="text-4xl text-[#00c2ab] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Growth</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Empowering careers and businesses to reach their full potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium CTA Conclusion */}
      <section className="relative bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white py-24 overflow-hidden">
        {/* Decorative Circles */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 rounded-full border-[20px] border-white/10 blur-sm"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full border-[30px] border-white/10 blur-md"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">Ready to Start Your Journey?</h2>
          <p className="text-xl md:text-2xl mb-12 text-teal-50 max-w-3xl mx-auto">
            Join thousands of professionals who have found their dream careers with Teztech.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link
              to="/jobs"
              className="w-full sm:w-auto bg-white text-[#00c2ab] px-10 py-5 rounded-2xl font-extrabold text-lg shadow-[0_10px_25px_rgba(0,0,0,0.15)] hover:shadow-[0_15px_35px_rgba(0,0,0,0.25)] transform hover:-translate-y-1 transition-all duration-300"
            >
              Browse Jobs
            </Link>
            <Link
              to="/register"
              className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-10 py-5 rounded-2xl font-extrabold text-lg hover:bg-white hover:text-[#00c2ab] transform hover:-translate-y-1 transition-all duration-300"
            >
              Sign Up Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
